import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { useAuth } from '../contexts/AuthContext';
import { hasPermission } from '../utils/permissions';

/**
 * Stock Report — per-product on-hand qty + last-received timestamp.
 * Sorted by on_hand_stock ascending so low-stock items float to the top.
 * Includes a "Movements" drawer that opens the audit trail for a product.
 */
const StockReport = () => {
  const { user } = useAuth();
  const canView = hasPermission(user, 'stock_report', 'view');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [lowStockOnly, setLowStockOnly] = useState(false);
  const [activeProduct, setActiveProduct] = useState(null);
  const [movements, setMovements] = useState([]);

  const fetchData = async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (search) params.search = search;
      if (lowStockOnly) params.low_stock_only = true;
      const r = await api.get('/stock-report', { params });
      setRows(r.data?.items || []);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to load stock report.');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovements = async (product) => {
    setActiveProduct(product);
    try {
      const r = await api.get(`/products/${product.id}/stock-movements`);
      setMovements(r.data?.items || []);
    } catch {
      setMovements([]);
    }
  };

  useEffect(() => {
    if (!canView) return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lowStockOnly, canView]);

  const fmt = (n) => Number(n || 0).toLocaleString();

  if (!canView) {
    return (
      <div className="p-6" data-testid="stock-report-denied">
        <h1 className="text-xl font-semibold text-rose-700">Access denied</h1>
        <p className="text-sm text-slate-600 mt-2">You do not have permission to view the Stock Report. Ask an admin to enable it under <span className="font-medium">User Rights → Admin → Stock Report</span>.</p>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-4" data-testid="stock-report-page">
      <div className="flex items-baseline justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">📦 Stock Report</h1>
          <p className="text-sm text-slate-600 mt-1">
            Live on-hand qty per product. Sorted lowest first — items at/below the threshold are flagged.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={lowStockOnly}
              onChange={(e) => setLowStockOnly(e.target.checked)}
              data-testid="stock-report-low-only"
            />
            Low stock only
          </label>
          <input
            type="text"
            placeholder="Search name / part #"
            className="border rounded-md px-3 py-1.5 text-sm"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && fetchData()}
            data-testid="stock-report-search"
          />
          <button
            onClick={fetchData}
            className="bg-indigo-600 text-white text-sm px-3 py-1.5 rounded-md hover:bg-indigo-700"
            data-testid="stock-report-refresh"
          >
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="p-3 bg-rose-50 border border-rose-200 rounded text-sm text-rose-800">{error}</div>
      )}

      <div className="border rounded-md overflow-hidden bg-white">
        <table className="w-full text-sm" data-testid="stock-report-table">
          <thead className="bg-slate-100 text-slate-700">
            <tr>
              <th className="px-3 py-2 text-left">Product</th>
              <th className="px-3 py-2 text-left">Part #</th>
              <th className="px-3 py-2 text-right">Opening</th>
              <th className="px-3 py-2 text-right">On Hand</th>
              <th className="px-3 py-2 text-right">Threshold</th>
              <th className="px-3 py-2 text-left">Last Received</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2"></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">Loading…</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={8} className="px-3 py-6 text-center text-slate-500">No products match.</td></tr>
            ) : (
              rows.map((r) => (
                <tr key={r.id} className={`border-t ${r.is_low_stock ? 'bg-amber-50/60' : ''} hover:bg-slate-50`}>
                  <td className="px-3 py-2 font-medium text-slate-900">{r.name}</td>
                  <td className="px-3 py-2 text-slate-700">{r.part_number || '—'}</td>
                  <td className="px-3 py-2 text-right text-slate-600">{fmt(r.opening_stock)}</td>
                  <td className={`px-3 py-2 text-right font-semibold ${r.is_low_stock ? 'text-amber-700' : 'text-slate-900'}`}>
                    {fmt(r.on_hand_stock)}
                  </td>
                  <td className="px-3 py-2 text-right text-slate-600">{fmt(r.low_stock_threshold)}</td>
                  <td className="px-3 py-2 text-slate-700">
                    {r.last_received_at ? new Date(r.last_received_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-3 py-2">
                    {r.is_low_stock ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-amber-100 text-amber-800 font-semibold">
                        ⚠ Low
                      </span>
                    ) : r.on_hand_stock <= 0 ? (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-rose-100 text-rose-800 font-semibold">
                        Out
                      </span>
                    ) : (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs bg-emerald-100 text-emerald-800 font-semibold">
                        OK
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">
                    <button
                      className="text-xs text-indigo-600 hover:underline"
                      onClick={() => fetchMovements(r)}
                      data-testid={`stock-report-movements-${r.id}`}
                    >
                      Movements ›
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {activeProduct && (
        <div className="fixed inset-0 bg-black/40 z-40 flex items-center justify-center p-4" onClick={() => setActiveProduct(null)}>
          <div className="bg-white rounded-lg max-w-3xl w-full max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="px-4 py-3 border-b flex items-center justify-between">
              <div>
                <div className="text-lg font-semibold">{activeProduct.name}</div>
                <div className="text-xs text-slate-500">Stock movement audit</div>
              </div>
              <button className="text-slate-500" onClick={() => setActiveProduct(null)}>✕</button>
            </div>
            <table className="w-full text-sm">
              <thead className="bg-slate-100 text-slate-700">
                <tr>
                  <th className="px-3 py-2 text-left">When</th>
                  <th className="px-3 py-2 text-right">Δ Qty</th>
                  <th className="px-3 py-2 text-left">Source</th>
                  <th className="px-3 py-2 text-left">Ref</th>
                  <th className="px-3 py-2 text-left">Note</th>
                </tr>
              </thead>
              <tbody>
                {movements.length === 0 ? (
                  <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">No movements yet.</td></tr>
                ) : movements.map((m) => (
                  <tr key={m.id} className="border-t">
                    <td className="px-3 py-2 text-slate-700 whitespace-nowrap">{new Date(m.ts).toLocaleString()}</td>
                    <td className={`px-3 py-2 text-right font-mono font-semibold ${m.qty_delta > 0 ? 'text-emerald-700' : 'text-rose-700'}`}>
                      {m.qty_delta > 0 ? '+' : ''}{m.qty_delta}
                    </td>
                    <td className="px-3 py-2 text-slate-700">{m.ref_kind}</td>
                    <td className="px-3 py-2 font-mono text-xs text-slate-600">{m.ref_id}</td>
                    <td className="px-3 py-2 text-slate-700 text-xs">{m.note || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default StockReport;
